import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	getAgreements,
	getAgreement,
	getParticipantSet,
	postReminder,
	putParticipantSet,
	putState,
} from '../../../services/axiosAdobe/_api';
import {
	POST_REMINDER,
	PUT_PARTICIPANT_SET,
	PUT_STATE,
} from '../../../services/axiosAdobe/_payloads';
import { SORT_TYPES } from './_types';
import Base from '../../Base/Base';
import { MESSAGE_BANNER_TYPES } from '../../../_types';
import { DataTable } from '../../../components/UI/MDC/DataTable/DataTable';
import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import { addMessage, clearMessages } from '../../../store/ancillary/actions';
import { deepCloneObject } from '../../../store/utility';
import { prettyDateFormat } from '../../../utils/strings/StringUtil';
import { isValidEmail } from '../../../utils/strings/StringUtil';

const Dashboard = () => {
	//state variables
	const dispatch = useDispatch();
	const [agreementsData, setAgreementsData] = useState([]);
	const [agreements, setAgreements] = useState([]);
	const [isEditing, setIsEditing] = useState(0);
	const [filter, setFilter] = useState('');
	const [loadingData, setLoadingData] = useState(true);
	const [pageError, setPageError] = useState(false);
	const [sortType, setSortType] = useState([SORT_TYPES.DEFAULT, 1]);

	/**
	 * initialize:  get data from API
	 * trigger: component load
	 */
	useEffect(() => {
		const initialize = async () => {
			await getAgreements()
				.then(response => {
					if (
						response &&
						response.data &&
						response.data.payload &&
						response.data.payload.userAgreementList
					) {
						setAgreementsData(
							response.data.payload.userAgreementList
						);
						setPageError(false);
					} else {
						setPageError(true);
						dispatch(
							addMessage({
								Key: MESSAGE_BANNER_TYPES.ERROR,
								Value: 'Data was unable to be received from document provider.',
							})
						);
					}
				})
				.catch(() => {
					setLoadingData(false);
					setPageError(true);
					dispatch(
						addMessage({
							Key: MESSAGE_BANNER_TYPES.ERROR,
							Value: 'Data was unable to be received from document provider.',
						})
					);
				});
		};
		dispatch(clearMessages());
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * translate raw data to state data
	 * trigger:  newly available API data
	 */
	useEffect(() => {
		const initialize = async () => {
			const fetchAgreementMetadata = async (
				agreementId,
				isActionable
			) => {
				const response = await getAgreement(agreementId);
				if (response) {
					// retrieve participant set details when more than one participant involved
					const responseParticipantSet =
						response.data?.payload?.participantSetsInfo?.[0]
							?.memberInfos?.length > 1
							? await getParticipantSet(
									response.data.payload.id,
									response.data.payload.participantSetsInfo[0]
										.id
							  )
							: null;
					const participantSet = responseParticipantSet
						? // filter out non-active (replaced) participants
						  responseParticipantSet.data?.payload?.memberInfos?.filter(
								member => member?.status === 'ACTIVE'
						  )
						: response.data.payload?.participantSetsInfo?.[0]
								?.memberInfos;
					return {
						ccs: response.data.payload.ccs
							? response.data.payload.ccs[0].email
							: '',
						participantSetId: isActionable
							? response.data.payload.participantSetsInfo[0].id
							: null,
						recipientParticipantIds: isActionable
							? response.data.payload.participantSetsInfo[0].memberInfos.map(
									mem => mem.id
							  )
							: [],
						signers: getPrettySigner(participantSet),
					};
				}
				return null;
			};

			let data = [];
			for (const agreement of agreementsData) {
				const [status, isActionable] = getPrettyStatus(
					agreement.status
				);
				const metadata = await fetchAgreementMetadata(
					agreement.id,
					isActionable
				);
				data.push({
					data: {
						Key: nanoid(),
						ID: agreement.id,
						Date: prettyDateFormat(agreement.displayDate),
						Document: agreement.name,
						Signers: metadata?.signers,
						OriginalSigners: metadata?.signers,
						ParticipantSetId: metadata?.participantSetId,
						RecipientParticipantIds:
							metadata?.recipientParticipantIds,
						Requestor: metadata?.ccs,
						Status: status,
					},
					props: {
						isEditable: false,
						isActionable: isActionable,
						isFiltered: false,
					},
				});
			}
			const sortedData = customSort(data);
			setAgreements(sortedData);
		};
		if (agreementsData) {
			initialize();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agreementsData]);

	useEffect(() => {
		if (agreements) {
			const newAgreements = deepCloneObject(agreements);
			const sortedAgreements = customSort(newAgreements);
			setAgreements(sortedAgreements);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortType]);

	/**
	 * update state based on user-defined filter
	 * trigger: new filter string
	 */
	useEffect(() => {
		if (agreements) {
			const newAgreements = deepCloneObject(agreements);

			newAgreements.forEach(agr => {
				let isFiltered = !Object.values(agr.data).some(dataValue => {
					let re = new RegExp(filter.toLowerCase());
					return dataValue && typeof dataValue === 'string'
						? re.test(dataValue.toLowerCase())
						: false;
				});

				agr.props.isFiltered = isFiltered;
			});

			setAgreements(newAgreements);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		if (agreements && agreements.length > 0) {
			setLoadingData(false);
		}
	}, [agreements]);

	///constants
	const HEADERS = ['Status', 'Date', 'Document', 'Signers', 'Requestor'];
	/**
	 * table headers for `DataTable`
	 */
	const tableHeaders = HEADERS.map((header, index) => {
		const currentSortingHeader = sortType[0].toLowerCase();
		const currentSortingDirection = sortType[1];

		return {
			Value: header,
			props: {
				onSortChange: sortOrder => {
					const newSortingHeader =
						sortOrder !== null
							? header.toUpperCase()
							: SORT_TYPES.DEFAULT;
					const newSortingDirection =
						sortOrder !== null ? sortOrder : 1;
					setSortType([newSortingHeader, newSortingDirection]);
				},
				sort:
					currentSortingHeader === header.toLowerCase()
						? currentSortingDirection
						: null,
			},
		};
	});

	///utility functions
	const customSort = data => {
		if (sortType && data) {
			const defaultSort = (a, b) => {
				if (
					a.data.Status === 'PENDING' &&
					b.data.Status !== 'PENDING'
				) {
					return -1;
				} else if (
					a.data.Status !== 'PENDING' &&
					b.data.Status === 'PENDING'
				) {
					return 1;
				} else {
					return 0;
				}
			};

			const dateSort = (a, b, direction) => {
				let aDate = new Date(a.data.Date),
					bDate = new Date(b.data.Date);
				return (bDate - aDate) * direction;
			};

			const documentSort = (a, b, direction) => {
				return (
					(a.data.Document.toLowerCase() >
					b.data.Document.toLowerCase()
						? 1
						: -1) * direction
				);
			};

			const signerSort = (a, b, direction) => {
				return (
					(a.data.Signers.toLowerCase() > b.data.Signers.toLowerCase()
						? 1
						: -1) * direction
				);
			};

			const statusSort = (a, b, direction) => {
				return (
					(a.data.Status.toLowerCase() > b.data.Status.toLowerCase()
						? 1
						: -1) * direction
				);
			};

			switch (sortType[0]) {
				case SORT_TYPES.STATUS:
					return data.sort((a, b) => statusSort(a, b, sortType[1]));
				case SORT_TYPES.DATE:
					return data.sort((a, b) => dateSort(a, b, sortType[1]));
				case SORT_TYPES.SIGNERS:
					return data.sort((a, b) => signerSort(a, b, sortType[1]));
				case SORT_TYPES.DOCUMENT:
					return data.sort((a, b) => documentSort(a, b, sortType[1]));
				case SORT_TYPES.DEFAULT:
				default:
					return data
						.sort((a, b) => dateSort(a, b, 1))
						.sort((a, b) => defaultSort(a, b, 1));
			}
		}
		return data;
	};

	/**
	 * Get the index of the agreements array which contains the key at `arr.data.Key`
	 * @param {Array} arr
	 * @param {string} key
	 * @returns int of index in the array where `key` is found (or -1 if not found)
	 */
	const containsKey = (arr, key) => {
		if (!!arr && Array.isArray(arr)) {
			let index = -1;
			arr.every((a, i) => {
				if (a.data && a.data.Key === key) {
					index = i;
					return false; //condition is met:  exit early
				}
				return true; //continue to search
			});
			return index;
		} else {
			return false;
		}
	};

	/**
	 * toggle editability property of an agreement (on/off)
	 * @param {string} rowKey unique key representing a row
	 */
	const toggleEditability = rowKey => {
		const newAgreements = deepCloneObject(agreements);
		const keyIndex = containsKey(newAgreements, rowKey);
		if (keyIndex > -1) {
			newAgreements[keyIndex].props.isEditable =
				!newAgreements[keyIndex].props.isEditable;
			setAgreements(newAgreements);
		}
	};

	/**
	 * convert list of raw member data into formatted string
	 * @param {object[]} set agreement's participant user set members
	 * @returns formatted string
	 */
	const getPrettySigner = set => {
		const signerSet = set ? set : [];
		let signerList = [];
		signerSet.forEach(signer => {
			signerList.push(signer.email);
		});
		return signerList.length > 0
			? signerList
					.join(';')
					.replace(/(^;)|(;$)/g, '')
					.trim()
			: '';
	};

	/**
	 * convert actual status to generalized status
	 * @param {string} status raw data status
	 * @returns one of a few status strings
	 */
	const getPrettyStatus = status => {
		let prettyStatus = 'PENDING';
		let isActionable = false;

		const pendingStatusPrefix = [
			'DELIVERED',
			'DOCUMENTS',
			'FORM',
			'OUT',
			'WAITING',
		];
		const completeStatusPrefix = ['ACCEPTED', 'APPROVED', 'SIGNED'];
		if (pendingStatusPrefix.some(prefix => status.startsWith(prefix))) {
			prettyStatus = 'PENDING';
			isActionable = true;
		} else if (
			completeStatusPrefix.some(prefix => status.startsWith(prefix))
		) {
			prettyStatus = 'COMPLETE';
		} else {
			prettyStatus = status;
		}

		return [prettyStatus, isActionable];
	};

	/**
	 * Validate the list of signers
	 * @param {string} signers
	 * @returns {Object} Validity and Error information
	 */
	const validateSigners = signers => {
		let valid = true;
		let additionalContext = '';
		const COUNT_THRESHOLD = 3;
		const splitSigners = signers
			.replace(/(^;)|(;$)/g, '')
			.trim()
			.split(';');

		const validCount = splitSigners.length <= COUNT_THRESHOLD;
		if (!validCount) {
			additionalContext = ' Please provide three (3) or fewer addresses.';
		}
		valid = valid && validCount;
		let validAddresses = true;

		if (valid) {
			validAddresses = splitSigners.every(s => {
				return isValidEmail(s);
			});
		}

		valid = valid && validAddresses;

		if (!valid) {
			if (signers.includes(',') || signers.includes(' ')) {
				additionalContext =
					' Use semicolon (;) to separate multiple email addresses.';
			}
		}

		return {
			Key: valid.toString(),
			Value: {
				ErrorMessage: valid
					? ''
					: 'Invalid email address(es).' + additionalContext,
			},
		};
	};

	///UI Handlers
	/**
	 * On-click handler of the abandon button
	 * @param {Event} e button-click event
	 * @param {object[]} rows list of all rows in the `DataTable`
	 * @param {object} row row which owns the button clicked
	 */
	const abandonButtonHandler = (e, rows, row) => {
		//decrement currently being edited count
		setIsEditing(isEditing - 1);

		const newAgreements = deepCloneObject(agreements);
		//find the original value and reset
		const keyIndex = containsKey(newAgreements, row.Key);
		if (keyIndex > -1) {
			newAgreements[keyIndex].data.Signers =
				newAgreements[keyIndex].data.OriginalSigners;
			newAgreements[keyIndex].props.isEditable =
				!newAgreements[keyIndex].props.isEditable;
			setAgreements(newAgreements);
		}
	};

	/**
	 * On-click handler of the edit button
	 * @param {Event} e button-click event
	 * @param {object[]} rows list of all rows in the `DataTable`
	 * @param {object} row row which owns the button clicked
	 */
	const editButtonHandler = (e, rows, row) => {
		toggleEditability(row.Key);
		setIsEditing(isEditing + 1);
	};

	/**
	 * On-click handler of the cancel agreement button
	 * @param {object} row row which owns the button clicked
	 * @returns void
	 */
	const removeButtonHandler = (_, __, row) => {
		const keyIndex = containsKey(agreements, row.Key);
		setLoadingData(true);
		dispatch(clearMessages());
		putState(agreements[keyIndex].data.ID, {
			...PUT_STATE,
			state: 'CANCELLED',
		})
			.then(() => {
				dispatch(
					addMessage({
						Key: MESSAGE_BANNER_TYPES.INFO,
						Value: 'Agreement has been cancelled.',
					})
				);
				let agreementsCopy = deepCloneObject(agreements);
				agreementsCopy[keyIndex].props.isActionable = false;
				agreementsCopy[keyIndex].data.Status = 'CANCELLED';
				agreementsCopy[keyIndex].data.Date = new Date(Date.now())
					.toLocaleDateString([], {
						day: 'numeric',
						month: 'numeric',
						year: 'numeric',
					})
					.replace(/\//g, '-')
					.toString();
				setAgreements(customSort(agreementsCopy));
			})
			.catch(() => {
				dispatch(
					addMessage({
						Key: MESSAGE_BANNER_TYPES.ERROR,
						Value: 'Unable to cancel agreement.',
					})
				);
			})
			.finally(() => {
				setLoadingData(false);
			});
	};

	/**
	 * On-click handler of the send reminder button
	 * @param {Event} e button-click event
	 * @param {object[]} rows list of all rows in the `DataTable`
	 * @param {object} row row which owns the button clicked     *
	 */
	const resendButtonHandler = (_, __, row) => {
		const keyIndex = containsKey(agreements, row.Key);
		dispatch(clearMessages());
		postReminder(agreements[keyIndex].data.ID, {
			...POST_REMINDER,
			recipientParticipantIds:
				agreements[keyIndex].data.RecipientParticipantIds,
		})
			.then(() => {
				dispatch(
					addMessage({
						Key: MESSAGE_BANNER_TYPES.INFO,
						Value:
							'A reminder email was sent to ' +
							agreements[keyIndex].data.Signers,
					})
				);
			})
			.catch(() => {
				dispatch(
					addMessage({
						Key: MESSAGE_BANNER_TYPES.ERROR,
						Value:
							'Unable to send reminder to ' +
							agreements[keyIndex].data.Signers,
					})
				);
			});
	};

	/**
	 * On-click handler of the submit button
	 * @param {Event} e button-click event
	 * @param {object[]} rows list of all rows in the `DataTable`
	 * @param {object} row row which owns the button clicked
	 */
	const submitButtonHandler = (e, rows, row) => {
		const valid = validateSigners(row.Cells.Signers.Value);
		if (valid) {
			toggleEditability(row.Key);
			setIsEditing(isEditing - 1);

			const keyIndex = containsKey(agreements, row.Key);
			if (keyIndex > -1) {
				const emailSet = [];
				agreements[keyIndex].data.Signers.split(';').forEach(e => {
					emailSet.push({
						email: e,
						id: '',
						securityOption: {
							authenticationMethod: '',
						},
					});
				});
				dispatch(clearMessages());
				putParticipantSet(
					agreements[keyIndex].data.ID,
					agreements[keyIndex].data.ParticipantSetId,
					{ ...PUT_PARTICIPANT_SET, memberInfos: emailSet }
				)
					.then(() => {
						dispatch(
							addMessage({
								Key: MESSAGE_BANNER_TYPES.INFO,
								Value: 'Agreement signer(s) has been updated.',
							})
						);
					})
					.catch(() => {
						//TODO - revert the value back to the original
						dispatch(
							addMessage({
								Key: MESSAGE_BANNER_TYPES.ERROR,
								Value: 'Attempted update of signer(s) was unsuccessful.',
							})
						);
					});
			}
		}
	};

	/**
	 * text input handler of the Signer textbox
	 * @param {Event} e input event
	 * @param {object[]} rows list of all rows in the `DataTable`
	 * @param {object} row row which owns the button clicked
	 */
	const onCellChangedHandler = (e, row, key) => {
		let newAgreements = deepCloneObject(agreements);
		const keyIndex = containsKey(newAgreements, row.Key);
		if (keyIndex > -1) {
			newAgreements[keyIndex].data[key] = e.target.value;
			setAgreements(newAgreements);
		}
	};

	const actions = {
		remind: {
			type: ACTION_TYPES.REMIND,
			label: 'Send Reminder',
			handler: resendButtonHandler,
		},
		edit: {
			type: ACTION_TYPES.EDIT,
			label: 'Edit',
			handler: editButtonHandler,
		},
		submit: {
			type: ACTION_TYPES.DONE,
			label: 'Submit',
			handler: submitButtonHandler,
		},
		cancel: {
			type: ACTION_TYPES.CANCEL,
			label: 'Abandon',
			handler: abandonButtonHandler,
		},
		remove: {
			type: ACTION_TYPES.REMOVE,
			label: 'Cancel',
			handler: removeButtonHandler,
		},
	};

	///UI Helpers
	/**
	 * Formats `actions` into something the `DataTable` can consume
	 * @returns actions for the `DataTable`
	 */
	const generateTableActions = () => {
		return Object.values(actions).reduce((prevValue, currValue) => {
			return [
				...prevValue,
				{
					Type: currValue.type,
					onChangedHandler: currValue.handler,
					disabled: currValue.disabled,
				},
			];
		}, []);
	};

	/**
	 * Formats `actions` into something the `DataTable` can consume as an Icon Legend
	 */
	const iconLegend = Object.values(actions).reduce(
		(prevValue, currValue, currIndex) => {
			return [
				...prevValue,
				{
					BindTo: currIndex,
					ValuePrimary: currValue.label,
				},
			];
		},
		[]
	);

	/**
	 * Formats `actions` into something the `DataTable` can consume as Action Cells
	 * @param {Object {bool, bool}} -  object containing `isEditable` and `isActionable`
	 * @returns action cells to be consumed by the `DataTable`
	 */
	const generateTableActionCells = ({ isEditable, isActionable }) => {
		if (isActionable) {
			return Object.values(actions).reduce(
				(prevValue, currValue, currIndex) => {
					const show = [ACTION_TYPES.REMIND, ACTION_TYPES.REMOVE];
					if (isEditable) {
						show.push(ACTION_TYPES.CANCEL);
						show.push(ACTION_TYPES.DONE);
					} else {
						show.push(ACTION_TYPES.EDIT);
					}
					if (show.includes(currValue.type)) {
						return [
							...prevValue,
							{
								BindTo: currIndex,
								Checked: false,
								Key: nanoid(),
								Text: currValue.label,
							},
						];
					} else {
						return prevValue;
					}
				},
				[]
			);
		} else {
			return [];
		}
	};

	/**
	 * Formats `actions` into something the `DataTable` can consume as Rows
	 * @param {object[]} agr list of agreements
	 * @returns rows for the `DataTable`
	 */
	const generateRows = agr => {
		let rows = [];
		agr &&
			agr.length > 0 &&
			agr
				.filter(a => {
					return !a.props.isFiltered;
				})
				.forEach(a => {
					const actionCells = generateTableActionCells(a.props);
					const signerValidation = a.props.isActionable
						? validateSigners(a.data.Signers)
						: { Key: true.toString(), Value: { ErrorMessage: '' } };
					rows.push({
						ActionCells: actionCells,
						Cells: {
							Date: {
								Value: a.data.Date,
								Validation: {
									Key: 'true',
								},
							},
							Document: {
								Value: a.data.Document,
								Validation: {
									Key: 'true',
								},
							},
							Signers: {
								Value: a.data.Signers,
								Validation: signerValidation,
								isEditable: a.props.isEditable,
							},
							Requestor: {
								Value: a.data.Requestor,
								Validation: { Key: 'true' },
							},
							Status: {
								Value: a.data.Status,
								Validation: {
									Key: 'true',
								},
							},
						},
						props: {
							//hide validation icons if row is not editable
							hideValidationIcon: a.props.isEditable !== true,
						},
						Key: a.data.Key,
					});
				});
		return rows;
	};

	return (
		<Base
			helpLink='placeholder'
			loading={loadingData}
			title={'Third-Party Organization Agreement Dashboard'}
			isError={pageError}
		>
			<DataTable
				indexed
				onTextFieldChangedHandler={e => setFilter(e.target.value)}
				iconLegend={iconLegend}
				rows={generateRows(agreements)}
				headers={tableHeaders}
				actions={generateTableActions()}
				label={'Agreements'}
				validation={
					!!isEditing
						? {
								onCellChangedHandler: onCellChangedHandler,
						  }
						: null
				}
			/>
		</Base>
	);
};

export default Dashboard;
